// §18.1 shared picker: TagInput (ST-2, §18.5 Detailed Expense "tags"). Selected tags render
// as removable chips; a native <select> (AccountPicker's precedent) adds more, with an
// inline "+ Create" per PT-6.
import { useState } from 'react';
import { useCreateTag, useTags } from '../lib/tags';
import { ghostButtonInlineClass, inputClass, selectClass } from './FormKit';

const CREATE_SENTINEL = '__create__';

export function TagInput({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string[];
  onChange: (tagIds: string[]) => void;
}): React.JSX.Element {
  const { data: tags } = useTags();
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');
  const createMutation = useCreateTag();

  const byId = new Map((tags ?? []).map((t) => [t.id, t]));
  const available = (tags ?? []).filter((t) => !t.archived && !value.includes(t.id));

  return (
    <div>
      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {value.map((tagId) => {
            const name = byId.get(tagId)?.name ?? tagId;
            return (
              <span
                key={tagId}
                className="inline-flex items-center gap-1 rounded-full bg-bg px-2 py-0.5 text-xs text-ink"
              >
                {name}
                <button
                  type="button"
                  aria-label={`Remove ${name}`}
                  className="text-muted hover:text-danger"
                  onClick={() => {
                    onChange(value.filter((v) => v !== tagId));
                  }}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {creating ? (
        <div className="flex gap-2">
          <input
            className={`${inputClass} flex-1`}
            aria-label="New tag name"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            autoFocus
          />
          <button
            type="button"
            className={ghostButtonInlineClass}
            disabled={createMutation.isPending}
            onClick={() => {
              const name = draft.trim();
              if (name === '') return;
              createMutation.mutate(
                { name },
                {
                  onSuccess: (created) => {
                    onChange([...value, created.id]);
                    setCreating(false);
                    setDraft('');
                  },
                },
              );
            }}
          >
            Create
          </button>
        </div>
      ) : (
        <select
          id={id}
          className={selectClass}
          aria-label="Add a tag"
          value=""
          onChange={(e) => {
            if (e.target.value === CREATE_SENTINEL) {
              setCreating(true);
              return;
            }
            if (e.target.value !== '') onChange([...value, e.target.value]);
          }}
        >
          <option value="">Add a tag…</option>
          {available.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
          <option value={CREATE_SENTINEL}>+ Create new…</option>
        </select>
      )}
    </div>
  );
}
